import React, { useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { Html } from '@react-three/drei'
import * as THREE from 'three'

function Node({ position, color, type, onClick }) {
  const meshRef = useRef()
  const glowRef = useRef()
  const [hovered, setHovered] = useState(false)
  
  // Custom neuron geometry with more detail
  const neuronGeometry = React.useMemo(() => {
    const radius = type === 'user' ? 0.8 : 0.4
    const geometry = new THREE.SphereGeometry(radius, 32, 32)
    
    // Add random displacement to vertices for more organic shape
    const positions = geometry.attributes.position.array
    for (let i = 0; i < positions.length; i += 3) {
      const noise = (Math.random() - 0.5) * 0.1
      positions[i] += noise
      positions[i + 1] += noise
      positions[i + 2] += noise
    }
    geometry.computeVertexNormals()
    return geometry
  }, [type])

  // Enhanced shader material for neuron core
  const neuronMaterial = React.useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        color: { value: new THREE.Color(color) },
        intensity: { value: type === 'user' ? 1.5 : 1.0 }
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;
        uniform float time;
        
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPosition = position;
          
          // Add subtle vertex animation
          vec3 pos = position;
          float displacement = sin(time * 2.0 + position.x * 10.0) * 0.02;
          pos += normal * displacement;
          
          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float time;
        uniform float intensity;
        varying vec3 vNormal;
        varying vec3 vPosition;
        
        void main() {
          // Core glow
          float pulse = sin(time * 2.0) * 0.1 + 0.9;
          float rim = pow(0.7 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 3.0);
          float glow = rim * pulse * intensity;
          
          // Energy patterns
          float energy = sin(vPosition.x * 10.0 + time) * 
                        sin(vPosition.y * 10.0 + time) * 
                        sin(vPosition.z * 10.0 + time);
          energy = abs(energy) * 0.3;
          
          // Combine effects
          vec3 finalColor = mix(color, vec3(1.0), rim * 0.5 + energy);
          float alpha = glow + 0.3;
          
          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending
    })
  }, [color, type])

  // Enhanced outer glow material
  const glowMaterial = React.useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        color: { value: new THREE.Color(color) },
        intensity: { value: type === 'user' ? 1.5 : 1.0 }
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;
        uniform float time;
        
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float time;
        uniform float intensity;
        varying vec3 vNormal;
        varying vec3 vPosition;
        
        void main() {
          float pulse = sin(time * 1.5) * 0.1 + 0.9;
          float rim = pow(0.6 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
          
          // Add energy field effect
          float energy = sin(vPosition.x * 5.0 + time) * 
                        sin(vPosition.y * 5.0 + time) * 
                        sin(vPosition.z * 5.0 + time);
          energy = abs(energy) * 0.2;
          
          float alpha = (rim * pulse + energy) * intensity * 0.5;
          vec3 finalColor = mix(color, vec3(1.0), energy * 0.5);
          
          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide
    })
  }, [color, type])

  // Animation
  useFrame((state) => {
    const t = state.clock.getElapsedTime()
    
    if (meshRef.current) {
      // Organic floating motion
      const floatX = Math.sin(t * 0.5 + position[0]) * 0.1
      const floatY = Math.cos(t * 0.4 + position[1]) * 0.1
      const floatZ = Math.sin(t * 0.3 + position[2]) * 0.1
      
      meshRef.current.position.set(
        position[0] + floatX,
        position[1] + floatY,
        position[2] + floatZ
      )

      // Scale pulse on hover with smooth transition
      const targetScale = hovered ? 1.4 : 1.0
      const currentScale = meshRef.current.scale.x
      const newScale = THREE.MathUtils.lerp(currentScale, targetScale, 0.1)
      meshRef.current.scale.setScalar(newScale)

      // Update shader uniforms
      meshRef.current.material.uniforms.time.value = t
      if (glowRef.current) {
        glowRef.current.position.copy(meshRef.current.position)
        glowRef.current.material.uniforms.time.value = t
        glowRef.current.scale.setScalar(newScale * 1.2)
      }
    }
  })

  return (
    <group>
      {/* Outer glow sphere */}
      <mesh
        ref={glowRef}
        geometry={neuronGeometry}
        material={glowMaterial}
        scale={1.8}
        position={position}
      />
      
      {/* Main neuron */}
      <mesh
        ref={meshRef}
        geometry={neuronGeometry}
        material={neuronMaterial}
        position={position}
        onClick={onClick}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        {/* Hover label */}
        {hovered && (
          <Html
            position={[0, type === 'user' ? 1.2 : 0.8, 0]}
            center
            style={{
              background: 'rgba(0,0,0,0.8)',
              padding: '6px 10px',
              borderRadius: '4px',
              color: 'white',
              fontSize: '12px',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)} Neuron
          </Html>
        )}
      </mesh>
    </group>
  )
}

export default Node
